import { useCallback, useEffect } from "react";
import { styled } from "styled-components";

import type { Knowledge } from "api/api.types";
import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { InfinityLoader, ListItemBox, LoadingCards } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { InteractiveDataItem } from "domains/knowledgeData/components/DataItem";
import { StyledTitle } from "domains/knowledgeFeed/components/shared";
import { getIsPinnedKnowledgePending } from "domains/pinAddReject/selectors";
import { ExpandableKnowledgeItem } from "domains/publication/components/ExpandableKnowledgeItem";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { SmallVideoItem } from "domains/videos/components/SmallVideoItem";
import { MediaType } from "domains/visualMediaPlayer/types";
import { getCanSeeSectionContent } from "entities/permissions/selectors";
import { createHasUnauthenticatedContentAccessSelector } from "entities/topics/selectors";

const StyledInteractiveDataItem = styled(InteractiveDataItem)`
  ${StyledTitle} {
    font-size: 1rem;
    font-weight: 500;
  }
`;

const SECTION_ID = "pinned-publications";
const NON_EXPANDED_ITEM_LIMIT = 3;

type PinnedKnowledgeSectionProps = {
  topicId: string;
  selectedSectionId?: string;
  selectSectionHandle: (id: string) => void;
};

export const PinnedKnowledgeSection = ({
  topicId,
  selectedSectionId,
  selectSectionHandle,
}: PinnedKnowledgeSectionProps) => {
  const isExpanded = SECTION_ID === selectedSectionId;
  const translate = useTranslate();
  const hasUnauthenticatedContentAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const isPending = useSelector(getIsPinnedKnowledgePending);
  const canSeeSection = useSelector(getCanSeeSectionContent);

  const {
    data: paginatedPinnedKnowledgeList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = knowledgeApiSlice.endpoints.getPinnedKnowledgeByTopic.useInfiniteQuery({ topicId });
  const pinnedKnowledgeList = paginatedPinnedKnowledgeList?.pages.flat() ?? [];

  const isHidden = selectedSectionId !== SECTION_ID && !!selectedSectionId;
  const isLocked = !hasUnauthenticatedContentAccess && /* hasAccess */ !canSeeSection;

  const onSelection = useCallback(() => selectSectionHandle(SECTION_ID), [selectSectionHandle]);

  const loadNextPage = async () => {
    if (hasNextPage && !isFetchingNextPage) await fetchNextPage();
  };

  const getVideoMediaType = (knowledge: Knowledge) => {
    if (knowledge.embedded_media !== undefined) return MediaType.videoViaEmbeddedHtmlPlayer;
    return MediaType.videoViaDirectFileLink;
  };

  const getVideoUrl = (knowledge: Knowledge) => {
    if (knowledge.embedded_media?.url) return knowledge.embedded_media.url;
    if ("attachment" in knowledge && !!knowledge.attachment) return knowledge.attachment.url;
    return undefined;
  };

  const renderKnowledgeItemByType = (knowledge: Knowledge) => {
    const getPathByKnowledgeType = (knowledgeType: string) => `${location.pathname}/${knowledgeType}/${knowledge.id}`;

    if (knowledge.embedded_media?.url || knowledge.attachment?.url) {
      return (
        <SmallVideoItem
          key={knowledge.id}
          id={knowledge.id}
          title={knowledge.title}
          summary={knowledge.summary ?? undefined}
          imageUrl={knowledge.images?.thumbnail ?? undefined}
          mediaType={getVideoMediaType(knowledge)}
          videoUrl={getVideoUrl(knowledge)}
          source={knowledge.source}
          timestamp={knowledge.time}
          link={getPathByKnowledgeType("videos")}
        />
      );
    }

    if (knowledge.interactive_link)
      return (
        <StyledInteractiveDataItem
          id={knowledge.id}
          title={knowledge.title}
          interactiveLink={knowledge.interactive_link}
          imageUrl={knowledge.images?.thumbnail}
          source={knowledge.source}
          timestamp={knowledge.time}
          detailLink={getPathByKnowledgeType("interactive-data")}
        />
      );

    return (
      <ExpandableKnowledgeItem
        id={knowledge.id}
        title={knowledge.title}
        summary={knowledge.summary}
        source={knowledge.source}
        signals={knowledge.signals ?? []}
        timestamp={knowledge.time}
        isBookmarked={!!knowledge.is_bookmarked}
        isPinned={!!knowledge.is_pinned}
        externalLink={knowledge.link}
        detailLink={getPathByKnowledgeType("publications")}
      />
    );
  };

  useEffect(() => {
    if (pinnedKnowledgeList.length !== 0 || !isExpanded) return;

    onSelection();
  }, [pinnedKnowledgeList.length, isExpanded, onSelection]);

  if (pinnedKnowledgeList.length === 0 && !isPending) {
    return null;
  }

  return (
    <TopicDetailSection
      title={translate("app.pinned_knowledge.title")}
      display
      topicId={topicId}
      selectSectionHandle={onSelection}
      isHidden={isHidden}
      isExpanded={isExpanded}
      isPending={isPending}
      isLocked={isLocked}
      hasNextPage={pinnedKnowledgeList.length > NON_EXPANDED_ITEM_LIMIT}
      iconVariant="pin"
    >
      <InfinityLoader loadNextPage={loadNextPage} hasNextPage={isExpanded && hasNextPage} isPending={isPending}>
        {pinnedKnowledgeList.slice(0, isExpanded ? undefined : NON_EXPANDED_ITEM_LIMIT).map((knowledge) => (
          <ListItemBox key={knowledge.id}>{renderKnowledgeItemByType(knowledge)}</ListItemBox>
        ))}
        {isPending && pinnedKnowledgeList.length === 0 && !isHidden && (
          <LoadingCards
            cardHeight={66}
            displayDetailButton
            imageHeight={50}
            imageWidth={50}
            lines={[30, 100]}
            numberOfItems={NON_EXPANDED_ITEM_LIMIT}
          />
        )}
      </InfinityLoader>
    </TopicDetailSection>
  );
};
